import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { Download, FileText, FileSpreadsheet, FileType, Filter } from "lucide-react";

const TABS = [
  { k: "fleet", label: "Flota" },
  { k: "expirations", label: "Vencimientos" },
  { k: "service-orders", label: "Órdenes" },
  { k: "leases", label: "Contratos" },
];

export default function Reports() {
  const { user } = useAuth();
  const [tab, setTab] = useState("fleet");

  const visibleTabs = TABS.filter((t) => {
    if (user.role === "workshop_admin") return t.k === "service-orders";
    return true;
  });

  return (
    <div data-testid="reports-page">
      <PageHeader
        tag="Operaciones"
        title="Reportes & Exportaciones"
        description="Filtra datos y exporta a CSV, Excel (XLSX) o PDF para presentaciones, auditorías o cierre mensual."
      />

      <div className="border-b border-gray-200 flex gap-1 mb-8 overflow-x-auto">
        {visibleTabs.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`report-tab-${t.k}`}
            className={`px-4 md:px-6 py-3 text-xs font-bold uppercase tracking-wider border-b-4 transition-colors ${
              tab === t.k ? "border-blue-600 text-black" : "border-transparent text-gray-500 hover:text-black"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "fleet" && <FleetReport />}
      {tab === "expirations" && <ExpirationsReport />}
      {tab === "service-orders" && <OrdersReport />}
      {tab === "leases" && <LeasesReport />}
    </div>
  );
}

function ExportButtons({ basePath, params, disabled }) {
  const download = (format) => {
    const qs = new URLSearchParams({ ...params, format }).toString();
    const url = `${process.env.REACT_APP_BACKEND_URL}/api${basePath}?${qs}`;
    window.open(url, "_blank");
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="export-buttons">
      <button onClick={() => download("csv")} disabled={disabled} className="h-12 px-4 border-2 border-black text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors disabled:opacity-40 flex items-center gap-2" data-testid="export-csv">
        <FileText size={14} strokeWidth={2.5} /> CSV
      </button>
      <button onClick={() => download("xlsx")} disabled={disabled} className="h-12 px-4 border-2 border-green-700 text-green-700 text-xs font-bold uppercase tracking-wider hover:bg-green-700 hover:text-white transition-colors disabled:opacity-40 flex items-center gap-2" data-testid="export-xlsx">
        <FileSpreadsheet size={14} strokeWidth={2.5} /> Excel
      </button>
      <button onClick={() => download("pdf")} disabled={disabled} className="h-12 px-4 border-2 border-blue-600 text-blue-600 text-xs font-bold uppercase tracking-wider hover:bg-blue-600 hover:text-white transition-colors disabled:opacity-40 flex items-center gap-2" data-testid="export-pdf">
        <FileType size={14} strokeWidth={2.5} /> PDF
      </button>
    </div>
  );
}

function ReportTable({ data }) {
  if (!data) return <div className="p-8 text-gray-500 text-sm">Cargando...</div>;
  if (data.count === 0) return <div className="border border-gray-200 p-12 text-center text-sm text-gray-500">Sin resultados con los filtros seleccionados.</div>;
  return (
    <div className="border border-gray-200 overflow-x-auto" data-testid="report-table">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>{data.headers.map((h, i) => <th key={i} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">{h}</th>)}</tr>
        </thead>
        <tbody>
          {data.rows.slice(0, 100).map((r, i) => (
            <tr key={i} className="border-b border-gray-200 hover:bg-gray-50">
              {r.map((c, j) => <td key={j} className="px-4 py-3 text-sm">{String(c ?? "")}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {data.rows.length > 100 && <p className="px-4 py-3 text-xs text-gray-500 bg-gray-50">Mostrando 100 de {data.rows.length}. Exporta para ver todo.</p>}
    </div>
  );
}

function FleetReport() {
  const [filters, setFilters] = useState({ status: "", workshop_id: "", year_min: "", year_max: "", brand: "" });
  const [data, setData] = useState(null);
  const [workshops, setWorkshops] = useState([]);

  useEffect(() => {
    api.get("/workshops").then((r) => setWorkshops(r.data)).catch(() => {});
  }, []);

  const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));

  const apply = async () => {
    const r = await api.get("/reports/fleet", { params: { ...params, format: "json" } });
    setData(r.data);
  };

  useEffect(() => { apply(); }, []);

  return (
    <div className="space-y-6">
      <FilterPanel>
        <SelectFilter label="Estado" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[
          { value: "", label: "Todos" },
          { value: "available", label: "Disponible" },
          { value: "leased", label: "Arrendado" },
          { value: "in_workshop", label: "En Taller" },
          { value: "out_of_service", label: "Fuera de Servicio" },
        ]} testId="filter-fleet-status" />
        <SelectFilter label="Taller" value={filters.workshop_id} onChange={(v) => setFilters({ ...filters, workshop_id: v })} options={[
          { value: "", label: "Todos" }, ...workshops.map((w) => ({ value: w.id, label: w.name })),
        ]} testId="filter-fleet-workshop" />
        <NumberFilter label="Año mínimo" value={filters.year_min} onChange={(v) => setFilters({ ...filters, year_min: v })} testId="filter-fleet-year-min" />
        <NumberFilter label="Año máximo" value={filters.year_max} onChange={(v) => setFilters({ ...filters, year_max: v })} testId="filter-fleet-year-max" />
        <TextFilter label="Marca" value={filters.brand} onChange={(v) => setFilters({ ...filters, brand: v })} testId="filter-fleet-brand" />
        <ApplyBtn onClick={apply} />
      </FilterPanel>
      <div className="flex justify-between items-center">
        <p className="veta-tag">Vista previa · {data?.count ?? 0} registro(s)</p>
        <ExportButtons basePath="/reports/fleet" params={params} disabled={(data?.count ?? 0) === 0} />
      </div>
      <ReportTable data={data} />
    </div>
  );
}

function ExpirationsReport() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const apply = async () => {
    const r = await api.get("/reports/expirations", { params: { days, format: "json" } });
    setData(r.data);
  };
  useEffect(() => { apply(); }, []);
  return (
    <div className="space-y-6">
      <FilterPanel>
        <SelectFilter label="Ventana" value={days} onChange={(v) => setDays(parseInt(v, 10))} options={[
          { value: 7, label: "Próximos 7 días" },
          { value: 30, label: "Próximos 30 días" },
          { value: 60, label: "Próximos 60 días" },
          { value: 90, label: "Próximos 90 días" },
          { value: 180, label: "Próximos 180 días" },
        ]} testId="filter-exp-days" />
        <ApplyBtn onClick={apply} />
      </FilterPanel>
      <div className="flex justify-between items-center">
        <p className="veta-tag">Vista previa · {data?.count ?? 0} item(s)</p>
        <ExportButtons basePath="/reports/expirations" params={{ days }} disabled={(data?.count ?? 0) === 0} />
      </div>
      <ReportTable data={data} />
    </div>
  );
}

function OrdersReport() {
  const [filters, setFilters] = useState({ status: "", category: "", date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const params = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""));
  const apply = async () => {
    const r = await api.get("/reports/service-orders", { params: { ...params, format: "json" } });
    setData(r.data);
  };
  useEffect(() => { apply(); }, []);
  return (
    <div className="space-y-6">
      <FilterPanel>
        <SelectFilter label="Estado" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={[
          { value: "", label: "Todos" },
          { value: "open", label: "Abierta" },
          { value: "in_progress", label: "En Progreso" },
          { value: "completed", label: "Completada" },
          { value: "cancelled", label: "Cancelada" },
        ]} testId="filter-order-status" />
        <SelectFilter label="Categoría" value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} options={[
          { value: "", label: "Todas" },
          { value: "maintenance", label: "Mantención" },
          { value: "repair", label: "Reparación" },
          { value: "service", label: "Servicio" },
          { value: "others", label: "Otros" },
        ]} testId="filter-order-category" />
        <DateFilter label="Desde" value={filters.date_from} onChange={(v) => setFilters({ ...filters, date_from: v })} testId="filter-order-from" />
        <DateFilter label="Hasta" value={filters.date_to} onChange={(v) => setFilters({ ...filters, date_to: v })} testId="filter-order-to" />
        <ApplyBtn onClick={apply} />
      </FilterPanel>
      <div className="flex justify-between items-center">
        <p className="veta-tag">Vista previa · {data?.count ?? 0} orden(es)</p>
        <ExportButtons basePath="/reports/service-orders" params={params} disabled={(data?.count ?? 0) === 0} />
      </div>
      <ReportTable data={data} />
    </div>
  );
}

function LeasesReport() {
  const [active, setActive] = useState("");
  const [data, setData] = useState(null);
  const params = active === "" ? {} : { active: active === "true" };
  const apply = async () => {
    const r = await api.get("/reports/leases", { params: { ...params, format: "json" } });
    setData(r.data);
  };
  useEffect(() => { apply(); }, []);
  return (
    <div className="space-y-6">
      <FilterPanel>
        <SelectFilter label="Estado" value={active} onChange={setActive} options={[
          { value: "", label: "Todos" },
          { value: "true", label: "Activos" },
          { value: "false", label: "Cerrados" },
        ]} testId="filter-lease-active" />
        <ApplyBtn onClick={apply} />
      </FilterPanel>
      <div className="flex justify-between items-center">
        <p className="veta-tag">Vista previa · {data?.count ?? 0} contrato(s)</p>
        <ExportButtons basePath="/reports/leases" params={params} disabled={(data?.count ?? 0) === 0} />
      </div>
      <ReportTable data={data} />
    </div>
  );
}

function FilterPanel({ children }) {
  return (
    <div className="border border-gray-200 p-5">
      <p className="veta-tag flex items-center gap-2 mb-4"><Filter size={12} strokeWidth={2.5} /> Filtros</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
    </div>
  );
}
function SelectFilter({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="veta-tag block mb-2">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-12 px-3 border border-gray-300 bg-white" data-testid={testId}>
        {options.map((o, i) => <option key={i} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function NumberFilter({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="veta-tag block mb-2">{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-12 px-3 border border-gray-300" data-testid={testId} />
    </div>
  );
}
function TextFilter({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="veta-tag block mb-2">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-12 px-3 border border-gray-300" data-testid={testId} />
    </div>
  );
}
function DateFilter({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="veta-tag block mb-2">{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-12 px-3 border border-gray-300" data-testid={testId} />
    </div>
  );
}
function ApplyBtn({ onClick }) {
  return (
    <div className="flex items-end">
      <button onClick={onClick} className="w-full h-12 bg-black text-white text-xs font-bold uppercase tracking-wider hover:bg-blue-600 active:scale-[0.98] transition-colors flex items-center justify-center gap-2" data-testid="apply-filters-btn">
        <Filter size={14} strokeWidth={2.5} /> Aplicar
      </button>
    </div>
  );
}
